// src/services/lineupService.js
// Service for managing event lineup (casting) via event_member_roles + team_members.

import { getSupabaseClient } from "./supabaseClient.js";

// Member type labels (for UI)
const ROLE_LABELS = {
    singer: "Cantante",
    bass: "Bajo",
    piano: "Teclado",
    guitar: "Guitarra",
    drummer: "Batería",
    technician: "Técnico de Sonido",
};

// Default role order for rendering
const ROLE_ORDER = ["singer", "bass", "piano", "guitar", "drummer", "technician"];

// Logistics constants
const SOUND_FREE_REGIONS = ["bizkaia"];
const PACO_REGIONS = ["bizkaia", "gipuzkoa", "araba", "cantabria"];
const DEFAULT_SOUND_PRICE = 600;
const DEFAULT_PACO_PRICE = 1000;

/**
 * List all active team_members for a given team.
 * Returns array of { id, display_name, member_type, is_active }
 */
export async function listLineupMembers(teamId) {
    if (!teamId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("team_members")
        .select("id, display_name, member_type, is_active")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("display_name", { ascending: true });

    if (error) throw error;
    return data || [];
}

/**
 * List the full team roster (team_members) including email, for admin management.
 * Returns array of { id, display_name, member_type, is_active, email, auth_user_id }
 */
export async function listTeamRoster(teamId) {
    if (!teamId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("team_members")
        .select("id, display_name, member_type, is_active, email, auth_user_id")
        .eq("team_id", teamId)
        .order("display_name", { ascending: true });

    if (error) throw error;
    return data || [];
}

/**
 * Update a team_member's email (admin only; RLS enforces it).
 * Pass null/empty to clear it.
 */
export async function updateTeamMemberEmail(memberId, email) {
    if (!memberId) throw new Error("memberId es obligatorio.");
    const supabase = getSupabaseClient();
    const value = email && email.trim() ? email.trim() : null;
    const { data, error } = await supabase
        .from("team_members")
        .update({ email: value })
        .eq("id", memberId)
        .select("id, display_name, email")
        .single();

    if (error) throw error;
    return data;
}

/**
 * List active team_members that have an email (candidates for internal messaging).
 * Returns array of { id, display_name, member_type, email }
 */
export async function listTeamMembersWithEmail(teamId) {
    if (!teamId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("team_members")
        .select("id, display_name, member_type, email")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .not("email", "is", null)
        .order("display_name", { ascending: true });

    if (error) throw error;
    return data || [];
}

/**
 * Get the lineup for a specific event.
 * Returns array of { role, member_id, member_name, member_type }
 */
export async function getEventLineup(eventId) {
    if (!eventId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("event_member_roles")
        .select("id, role, member_id, team_members(id, display_name, member_type)")
        .eq("event_id", eventId);

    if (error) throw error;
    return (data || []).map(r => ({
        id: r.id,
        role: r.role,
        member_id: r.member_id,
        member_name: r.team_members?.display_name || "—",
        member_type: r.team_members?.member_type || null,
    }));
}

/**
 * Get the lineup for an event including each member's email.
 * Used for internal calendar invites: resolves event_member_roles -> team_members (email).
 * Members without an email are still returned (email: null) so the UI can warn/skip them.
 * Returns array of { role, member_id, member_name, member_type, email }
 */
export async function getEventLineupWithEmails(eventId) {
    if (!eventId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("event_member_roles")
        .select("id, role, member_id, team_members(id, display_name, member_type, email)")
        .eq("event_id", eventId);

    if (error) throw error;
    return (data || []).map(r => ({
        id: r.id,
        role: r.role,
        member_id: r.member_id,
        member_name: r.team_members?.display_name || "—",
        member_type: r.team_members?.member_type || null,
        email: r.team_members?.email || null,
    }));
}

/**
 * Save the lineup for an event.
 * assignments: array of { role, member_id } — roles without a member_id are skipped.
 * Deletes all existing lineup entries and re-inserts.
 */
export async function saveEventLineup(teamId, eventId, assignments) {
    if (!eventId || !teamId) return;
    const supabase = getSupabaseClient();

    // 1. Delete existing lineup
    const { error: delErr } = await supabase
        .from("event_member_roles")
        .delete()
        .eq("event_id", eventId);
    if (delErr) throw delErr;

    // 2. Filter valid assignments
    const valid = (assignments || []).filter(a => a.role && a.member_id);
    if (!valid.length) return;

    // 3. Insert
    const rows = valid.map(a => ({
        team_id: teamId,
        event_id: eventId,
        member_id: a.member_id,
        role: a.role,
    }));

    const { error: insErr } = await supabase
        .from("event_member_roles")
        .insert(rows);
    if (insErr) throw insErr;
}

/**
 * Get default lineup (titulares) for a team.
 * Maps each role to the first active member with that member_type.
 * Returns map { role: member_id }
 */
export async function getDefaultLineup(teamId) {
    const members = await listLineupMembers(teamId);
    const defaults = {};

    // Specific defaults by name (more reliable than just member_type)
    const TITULARES = {
        singer: "Pabs",
        bass: "Luco",
        piano: "Barc",
        guitar: "Mote",
        drummer: "Unaiko",
        technician: "Xabi Iriarte",
    };

    for (const role of ROLE_ORDER) {
        const titular = TITULARES[role];
        // Try to find by name first
        const byName = members.find(m =>
            m.display_name?.toLowerCase().includes(titular?.toLowerCase() || "___nope___")
        );
        if (byName) {
            defaults[role] = byName.id;
            continue;
        }
        // Fallback: first member with matching type
        const byType = members.find(m => m.member_type === role);
        if (byType) {
            defaults[role] = byType.id;
        }
    }

    return defaults;
}

/**
 * Get logistics data for a specific event.
 * Returns { has_own_sound, sound_price, has_paco_stage, paco_stage_price }
 */
export async function getEventLogistics(eventId) {
    if (!eventId) return { has_own_sound: false, sound_price: null, has_paco_stage: false, paco_stage_price: null };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("events")
        .select("has_own_sound, sound_price, has_paco_stage, paco_stage_price")
        .eq("id", eventId)
        .single();

    if (error) throw error;
    return data || { has_own_sound: false, sound_price: null, has_paco_stage: false, paco_stage_price: null };
}

/**
 * Save logistics data for a specific event.
 */
export async function saveEventLogistics(eventId, logistics) {
    if (!eventId) return;
    const supabase = getSupabaseClient();
    const { error } = await supabase
        .from("events")
        .update({
            has_own_sound: logistics.has_own_sound || 'no', // Default to 'no'
            sound_price: (logistics.has_own_sound && logistics.has_own_sound !== 'no') ? (Number(logistics.sound_price) || 0) : null,
            has_paco_stage: logistics.has_paco_stage || 'no', // Default to 'no'
            paco_stage_price: (logistics.has_paco_stage && logistics.has_paco_stage !== 'no') ? (Number(logistics.paco_stage_price) || 0) : null,
        })
        .eq("id", eventId);

    if (error) throw error;
}

/**
 * Get all event IDs where a specific member is assigned.
 * Useful for the "Mis Eventos" filter.
 */
export async function getEventIdsByMember(memberId) {
    if (!memberId) return [];
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
        .from("event_member_roles")
        .select("event_id")
        .eq("member_id", memberId);

    if (error) throw error;
    return (data || []).map(r => r.event_id);
}

/**
 * Get team_member ID for the current auth user in a team.
 */
export async function getTeamMemberByAuthUser(teamId) {
    if (!teamId) return null;
    const supabase = getSupabaseClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
        .from("team_members")
        .select("id, display_name, member_type")
        .eq("team_id", teamId)
        .eq("auth_user_id", user.id)
        .single();

    if (error) return null; // Not linked
    return data;
}

export { ROLE_LABELS, ROLE_ORDER, SOUND_FREE_REGIONS, PACO_REGIONS, DEFAULT_SOUND_PRICE, DEFAULT_PACO_PRICE };
